/**
 * Reveal khi cuộn tới — bản React của `data-reveal` + IntersectionObserver trong thiết kế
 * (`observe()` của khối `<script type="text/x-dc">`, template.html dòng 1374–1393).
 *
 * Đây là CONTROLLER Ở MỨC DOM, không phải hook gắn với cây React: nó tìm node bằng
 * `querySelectorAll('[data-reveal]')` nên không quan tâm ai render ra node đó, và cả trang chỉ
 * cần MỘT observer thay vì một observer cho mỗi section.
 *
 * Trình tự tránh HAI cái bẫy của SSG (design doc §6.1):
 *
 *   gắn      → observe, KHÔNG đổi style gì
 *              (JS lỗi / CSP chặn / crawler không chạy JS ⇒ mọi thứ vẫn hiện)
 *   callback đầu, cho MỌI node được observe:
 *     trong khung nhìn  → đánh dấu xong, unobserve. KHÔNG bao giờ bị ẩn ⇒ không nháy.
 *     ngoài khung nhìn  → thêm class ẩn. Nó nằm dưới màn, không ai thấy lúc ẩn.
 *   cuộn tới  → bỏ class ẩn ⇒ CSS transition chạy, unobserve.
 *
 * Tên file giữ theo tên trong plan/design doc (`useReveal`) dù ở đây KHÔNG có hook React nào —
 * đổi tên sẽ làm mọi tham chiếu trong hai tài liệu đó trỏ hụt.
 *
 * `prefers-reduced-motion: reduce` ⇒ hàm này không làm gì cả. Đó chỉ là lớp thứ hai: lớp canh
 * thật sự là `@media (prefers-reduced-motion: reduce)` trong `motion.css`, vì nó còn đúng cả khi
 * user đổi thiết lập SAU khi trang đã tải (hàm này chỉ đọc media query đúng một lần).
 */
export const REVEAL_HIDDEN = 'nx-reveal-hidden';

/** Đo từ thiết kế: `{ rootMargin: '0px 0px -8% 0px', threshold: 0.08 }`. */
const IO_OPTIONS: IntersectionObserverInit = {
  rootMargin: '0px 0px -8% 0px',
  threshold: 0.08,
};

/**
 * Số bậc stagger trước khi quay vòng. Thiết kế viết `Math.min(i % 6, 5) * 55ms` — phép `min` là
 * thừa vì `i % 6` đã nằm trong 0..5, nên chỗ này chỉ giữ lại phần có tác dụng: quay vòng 6 bậc.
 * Con số 55ms sống DUY NHẤT trong `motion.css`; ở đây chỉ truyền chỉ số.
 */
const STAGGER_STEPS = 6;

/**
 * Node đã đi qua lượt callback đầu tiên, nhớ theo NODE chứ không theo lần gọi `revealAll`.
 * Nhờ vậy gọi lại `revealAll()` (lô sau nạp thêm nội dung) không ẩn lại thứ người dùng đã đọc và
 * đã cuộn qua. `WeakSet` nên node bị gỡ khỏi DOM không giữ bộ nhớ lại.
 */
const firstPassDone = new WeakSet<Element>();

/**
 * Node có nằm HOÀN TOÀN ngoài khung nhìn không — tức không ai đang nhìn thấy nó.
 *
 * Không dùng `isIntersecting` cho việc này: `rootMargin: '0px 0px -8% 0px'` cộng `threshold`
 * 0.08 xếp cả khối đang lộ ở dải đáy màn vào nhóm "không giao" (đó là chủ đích — để reveal nổ
 * SỚM hơn một chút). Ẩn theo tiêu chí đó thì phần đang nhìn thấy ở mép dưới sẽ mờ đi rồi tụt
 * xuống ngay sau khi tải: đúng cái nháy của bẫy 2, chỉ dời xuống đáy màn.
 *
 * Chỉ xét trục dọc: trang không có cuộn ngang (luật 4 của skill design-system-web).
 */
function isOutOfSight(item: IntersectionObserverEntry): boolean {
  const rect = item.boundingClientRect;
  const viewport = window.innerHeight || document.documentElement.clientHeight;
  return rect.top >= viewport || rect.bottom <= 0;
}

function prefersReducedMotion(): boolean {
  if (typeof matchMedia !== 'function') return false;
  return matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Gắn reveal cho mọi `[data-reveal]` bên trong `root`. Trả về hàm dọn.
 *
 * Gọi lại được nhiều lần — mỗi lần tạo một observer riêng, còn trạng thái "đã qua lượt đầu" thì
 * dùng chung theo node.
 */
export function revealAll(root?: ParentNode): () => void {
  // `document` phải được đọc TRONG thân hàm, không qua tham số mặc định: tham số mặc định được
  // tính trước thân hàm, nên bản cũ ném ReferenceError trong Node (lúc prerender) trước khi kịp
  // chạm cổng môi trường ở ngay dưới.
  const scope = root ?? (typeof document === 'undefined' ? undefined : document);
  if (scope === undefined) return () => undefined;
  if (typeof IntersectionObserver === 'undefined') return () => undefined;
  if (prefersReducedMotion()) return () => undefined;

  const observer = new IntersectionObserver((entries, self) => {
    for (const item of entries) {
      const el = item.target as HTMLElement;

      if (!firstPassDone.has(el)) {
        firstPassDone.add(el);
        if (isOutOfSight(item)) {
          el.classList.add(REVEAL_HIDDEN);
        } else {
          // Người dùng đang nhìn thấy nó ⇒ để yên. Đây là chỗ chống nháy.
          self.unobserve(el);
        }
        continue;
      }

      if (item.isIntersecting) {
        el.classList.remove(REVEAL_HIDDEN);
        self.unobserve(el);
      }
    }
  }, IO_OPTIONS);

  // Chính `root` cũng được xét: `revealAll(node)` cho một khối vừa nạp mà node đó tự mang dấu
  // hiệu thì bỏ qua nó là bỏ sót im lặng.
  const rootItself = scope instanceof HTMLElement && scope.matches('[data-reveal]') ? [scope] : [];
  const nodes = [...rootItself, ...scope.querySelectorAll<HTMLElement>('[data-reveal]')];
  nodes.forEach((node, index) => {
    node.style.setProperty('--nx-reveal-i', String(index % STAGGER_STEPS));
    observer.observe(node);
  });

  return () => {
    observer.disconnect();
  };
}
